import { useEffect, useRef, useCallback } from "react";

const blockers = [];
let isPatched = false;
let originalPushState = null;
let originalReplaceState = null;
let lastUrl = typeof window !== "undefined" ? window.location.href : "";

function getActiveMessage() {
  if (!blockers.length) return null;
  return blockers[blockers.length - 1].getMessage();
}

function onPopState() {
  const msg = getActiveMessage();
  if (!msg) {
    lastUrl = window.location.href;
    return;
  }

  const ok = window.confirm(msg);
  if (!ok) {
    try {
      history.pushState(null, "", lastUrl);
    } catch {
      history.go(1);
    }
  } else {
    lastUrl = window.location.href;
  }
}

function patchHistory() {
  if (typeof window === "undefined" || isPatched) return;

  originalPushState = history.pushState.bind(history);
  originalReplaceState = history.replaceState.bind(history);
  lastUrl = window.location.href;

  history.pushState = function (...args) {
    const msg = getActiveMessage();
    if (msg && !window.confirm(msg)) return;
    const ret = originalPushState.apply(this, args);
    lastUrl = window.location.href;
    return ret;
  };

  history.replaceState = function (...args) {
    const msg = getActiveMessage();
    if (msg && !window.confirm(msg)) return;
    const ret = originalReplaceState.apply(this, args);
    lastUrl = window.location.href;
    return ret;
  };

  window.addEventListener("popstate", onPopState);
  isPatched = true;
}

function restoreHistoryIfNoBlockers() {
  if (!isPatched) return;
  if (blockers.length > 0) return;
  history.pushState = originalPushState;
  history.replaceState = originalReplaceState;
  window.removeEventListener("popstate", onPopState);
  isPatched = false;
}

/**
 * Hook that warns before leaving when `when` is true.
 * Handles: browser close/refresh + SPA navigation.
 */

export default function useUnsavedChangesWarning(
  when,
  message = "⚠️ You have unsaved changes. Are you sure you want to leave?"
) {
  const messageRef = useRef(message);
  messageRef.current = message;

  const getMessage = useCallback(() => messageRef.current, []);
  const myBlocker = useRef({ getMessage });

  // Manage blocker registration
  useEffect(() => {
    if (when) {
      if (!blockers.includes(myBlocker.current)) {
        blockers.push(myBlocker.current);
      }
      patchHistory();
    } else {
      const i = blockers.indexOf(myBlocker.current);
      if (i !== -1) blockers.splice(i, 1);
      restoreHistoryIfNoBlockers();
    }

    return () => {
      const i = blockers.indexOf(myBlocker.current);
      if (i !== -1) blockers.splice(i, 1);
      restoreHistoryIfNoBlockers();
    };
  }, [when]);

  // Handle browser close / refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!when) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [when]);
}
